import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, btnPrimary, btnSecondary } from '../styles/theme.js'
import { sessions as sessionsApi, tags as tagsApi, projects as projectsApi, rubrics as rubricsApi, searchHistoryApi } from '../lib/api.js'

// --- Styles ---

const MOBILE = '(max-width: 768px)'

const browserWrap = css`
  display: flex;
  flex-direction: column;
`

const toolbar = css`
  display: flex;
  align-items: center;
  gap: ${space.md};
  padding: ${space.md} 0;
  flex-shrink: 0;
  flex-wrap: wrap;
  @media ${MOBILE} {
    gap: ${space.sm};
  }
`

const searchWrap = css`
  position: relative;
  max-width: 400px;
  flex: 1;
  @media ${MOBILE} {
    max-width: none;
    flex-basis: 100%;
    order: -1;
  }
`

const searchInput = css`
  ${input};
  max-width: 400px;
  @media ${MOBILE} {
    max-width: none;
    font-size: ${font.size.sm};
  }
`

const sortSelect = css`
  padding: ${space.sm} ${space.md};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  font-size: ${font.size.sm};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  cursor: pointer;
  @media ${MOBILE} {
    padding: ${space.xs} ${space.sm};
    font-size: ${font.size.xs};
  }
`

const counterStyle = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-left: auto;
  white-space: nowrap;
  @media ${MOBILE} {
    flex-basis: 100%;
    text-align: right;
    margin-left: 0;
    font-size: ${font.size.xs};
  }
`

const filterPanelStyle = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.sm} 0;
  flex-shrink: 0;
  flex-wrap: wrap;
  @media ${MOBILE} {
    gap: ${space.xs};
  }
`

const filterLabel = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  font-weight: ${font.weight.medium};
`

const filterInput = css`
  padding: 4px ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  width: 130px;
  &:focus { border-color: ${colors.borderFocus}; outline: none; }
  @media ${MOBILE} {
    width: 100px;
  }
`

const filterDivider = css`
  width: 1px;
  height: 20px;
  background: ${colors.border};
  margin: 0 ${space.xs};
  @media ${MOBILE} {
    display: none;
  }
`

const clearFiltersBtn = css`
  padding: 3px ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-family: ${font.family};
  background: none;
  color: ${colors.textMuted};
  cursor: pointer;
  &:hover { color: ${colors.text}; background: ${colors.bg}; }
`

const activeFilterCount = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${colors.brand};
  color: white;
  font-size: 10px;
  font-weight: ${font.weight.semibold};
  border-radius: 50%;
  width: 16px;
  height: 16px;
  margin-left: 4px;
`

const toggleFiltersBtn = css`
  padding: ${space.sm} ${space.md};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  font-size: ${font.size.sm};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  &:hover { background: ${colors.bg}; }
  @media ${MOBILE} {
    padding: ${space.xs} ${space.sm};
    font-size: ${font.size.xs};
  }
`

const sessionList = css`
  border: 1px solid ${colors.border};
  border-radius: 8px;
  background: ${colors.bgCard};
`

const sessionRow = css`
  display: flex;
  align-items: flex-start;
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  cursor: pointer;
  transition: background 0.1s ease;
  &:last-child { border-bottom: none; }
  &:hover { background: ${colors.bg}; }
  @media ${MOBILE} {
    padding: ${space.sm};
  }
`

const sessionRowSelected = css`
  background: ${colors.brandLight} !important;
`

const sessionInfo = css`
  flex: 1;
  min-width: 0;
`

const sessionIdStyle = css`
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
  color: ${colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const sessionMeta = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-top: 2px;
  display: flex;
  gap: ${space.md};
  @media ${MOBILE} {
    gap: ${space.sm};
  }
`

const previewStyle = css`
  font-size: ${font.size.xs};
  color: ${colors.textSecondary};
  margin-top: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
  @media ${MOBILE} {
    -webkit-line-clamp: 1;
  }
`

const tagChip = css`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  margin-left: ${space.sm};
`

const tagChipsDesktop = css`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  justify-content: flex-end;
  @media ${MOBILE} {
    display: none;
  }
`

const tagCountBadge = css`
  display: none;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  background: ${colors.border};
  color: ${colors.textSecondary};
  white-space: nowrap;
  @media ${MOBILE} {
    display: inline-block;
  }
`


const paginationBar = css`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${space.md};
  padding: ${space.md} 0;
  flex-shrink: 0;
  @media ${MOBILE} {
    gap: ${space.sm};
  }
`

const emptyState = css`
  text-align: center;
  padding: ${space['2xl']};
  color: ${colors.textMuted};
`

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.sm};
  a { color: ${colors.textSecondary}; }
`

const deleteBtn = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  border-radius: 4px;
  margin-left: ${space.sm};
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.1s;
  .${sessionRow}:hover & { opacity: 1; }
  &:hover { color: ${colors.critical}; background: ${colors.critical}10; }
`

const rightCol = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  margin-left: ${space.md};
  flex-shrink: 0;
  @media ${MOBILE} {
    margin-left: ${space.sm};
  }
`

// --- Tag filter styles ---

const tagFilterContainer = css`
  position: relative;
`

const tagFilterBtn = css`
  padding: ${space.sm} ${space.md};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  font-size: ${font.size.sm};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover { background: ${colors.bg}; }
`

const tagDropdown = css`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  width: 280px;
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  z-index: 50;
  overflow: hidden;
`

const tagSearchInput = css`
  width: 100%;
  padding: ${space.sm} ${space.md};
  border: none;
  border-bottom: 1px solid ${colors.border};
  font-size: ${font.size.sm};
  font-family: ${font.family};
  background: transparent;
  color: ${colors.text};
  outline: none;
  box-sizing: border-box;
  &::placeholder { color: ${colors.textMuted}; }
`

const tagDropdownList = css`
  max-height: 240px;
  overflow-y: auto;
`

const tagDropdownItem = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: 6px ${space.md};
  cursor: pointer;
  font-size: ${font.size.sm};
  transition: background 0.08s;
  &:hover { background: ${colors.bg}; }
`

const tagDropdownSwatch = css`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
`

const tagDropdownName = css`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const tagStateIndicator = css`
  font-size: 11px;
  font-weight: ${font.weight.semibold};
  width: 18px;
  height: 18px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const selectedTagChips = css`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: ${space.sm};
`

const selectedTagChip = css`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  cursor: pointer;
  user-select: none;
  &:hover { opacity: 0.8; }
`

const tagModeToggle = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid ${colors.border};
  background: ${colors.bgCard};
  &:hover { color: ${colors.text}; }
`

// --- Component ---

export function BrowserView() {
  let projectId = null
  let project = null
  let sessionItems = []
  let total = 0
  let loading = false
  let search = ''
  let searchDebounce = null
  let recordDebounce = null
  let sort = 'newest'
  // Tag filter: 'include' or 'exclude' per tag id
  let includeTagIds = new Set()
  let excludeTagIds = new Set()
  let tagMode = 'any'  // 'any' or 'all'
  let allTags = []
  let page = 0
  let selectedIndex = -1
  let isOwner = false
  const PAGE_SIZE = 50

  // Tag dropdown state
  let tagDropdownOpen = false
  let tagSearch = ''

  // Search history state
  let searchDropdownOpen = false
  let recentSearches = []
  let savedSearches = []
  let savingSearch = false
  let saveSearchName = ''

  // Advanced filters
  let showFilters = false
  let dateFrom = ''
  let dateTo = ''
  let minMessages = ''
  let maxMessages = ''
  let filterUntagged = false

  // Bookmark & my activity filters
  let filterBookmarked = false
  let filterMyActivity = false

  // Evaluation filter
  let evalFilter = 'any' // 'any' | 'evaluated' | 'unevaluated'
  let evalRubricId = ''
  let rubricList = []

  async function loadProject() {
    try {
      const proj = await projectsApi.get(projectId)
      project = proj
      isOwner = proj.memberRole === 'owner'
      m.redraw()
    } catch { /* ignore */ }
  }

  async function deleteSession(sessionId, e) {
    e.stopPropagation()
    if (!confirm('Delete this session and all its data?')) return
    try {
      await sessionsApi.delete(projectId, sessionId)
      sessionItems = sessionItems.filter(s => s.id !== sessionId)
      total--
      m.redraw()
    } catch (err) {
      console.error('Failed to delete session:', err)
    }
  }

  async function loadTags() {
    try {
      allTags = await tagsApi.list(projectId)
    } catch { allTags = [] }
  }

  async function loadRubrics() {
    try {
      rubricList = await rubricsApi.list(projectId)
    } catch { rubricList = [] }
  }

  async function loadSearchHistory() {
    try {
      const [recent, saved] = await Promise.all([
        searchHistoryApi.list(projectId, { limit: 10 }),
        searchHistoryApi.listSaved(projectId),
      ])
      recentSearches = recent || []
      savedSearches = saved || []
      m.redraw()
    } catch { /* ignore */ }
  }

  function applySearch(entry) {
    search = entry.query || ''
    if (entry.filters) {
      const f = typeof entry.filters === 'string' ? JSON.parse(entry.filters) : entry.filters
      if (f.tagIds) { includeTagIds = new Set(f.tagIds.split ? f.tagIds.split(',') : f.tagIds); tagMode = f.tagMode || 'any' }
      if (f.excludeTagIds) excludeTagIds = new Set(f.excludeTagIds.split ? f.excludeTagIds.split(',') : f.excludeTagIds)
      if (f.dateFrom) dateFrom = f.dateFrom
      if (f.dateTo) dateTo = f.dateTo.replace('T23:59:59', '')
      if (f.untagged) filterUntagged = true
      if (f.bookmarked) filterBookmarked = true
      if (f.myActivity) filterMyActivity = true
      if (f.evaluated === 'yes') evalFilter = 'evaluated'
      else if (f.evaluated === 'no') evalFilter = 'unevaluated'
      else if (f.evaluated === 'failed_critical') evalFilter = 'failed_critical'
      else if (f.evaluated === 'failed_important') evalFilter = 'failed_important'
      if (f.evalRubricId) evalRubricId = f.evalRubricId
      showFilters = true
    }
    searchDropdownOpen = false
    page = 0
    loadSessions()
  }

  async function saveCurrentSearch() {
    if (!saveSearchName.trim()) return
    try {
      await searchHistoryApi.save(projectId, {
        name: saveSearchName.trim(),
        query: search.trim(),
        filters: buildParams(),
      })
      saveSearchName = ''
      savingSearch = false
      loadSearchHistory()
    } catch (err) {
      console.error('Failed to save search:', err)
    }
  }

  async function deleteSavedSearch(id, e) {
    e.stopPropagation()
    try {
      await searchHistoryApi.deleteSaved(projectId, id)
      savedSearches = savedSearches.filter(s => s.id !== id)
      m.redraw()
    } catch { /* ignore */ }
  }

  function buildQueryString() {
    const params = new URLSearchParams()
    if (search.trim()) params.set('q', search.trim())
    if (sort !== 'newest') params.set('sort', sort)
    if (includeTagIds.size > 0) params.set('tags', [...includeTagIds].join(','))
    if (excludeTagIds.size > 0) params.set('xtags', [...excludeTagIds].join(','))
    if (tagMode !== 'any') params.set('tagMode', tagMode)
    if (dateFrom) params.set('from', dateFrom)
    if (dateTo) params.set('to', dateTo)
    if (minMessages !== '') params.set('minMsg', minMessages)
    if (maxMessages !== '') params.set('maxMsg', maxMessages)
    if (filterUntagged) params.set('untagged', '1')
    if (filterBookmarked) params.set('bm', '1')
    if (filterMyActivity) params.set('mine', '1')
    if (evalFilter !== 'any') params.set('eval', evalFilter)
    if (evalRubricId) params.set('rubric', evalRubricId)
    if (page > 0) params.set('p', String(page))
    // Replace '+' with '%20': URLSearchParams encodes spaces as '+' but
    // Mithril's m.parseQueryString uses decodeURIComponent which doesn't decode '+'
    const qs = params.toString().replace(/\+/g, '%20')
    return qs ? '?' + qs : ''
  }

  function syncUrlToState() {
    const base = `#!/projects/${projectId}/browser`
    const qs = buildQueryString()
    const newHash = base + qs
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash)
    }
  }

  function restoreStateFromUrl(attrs) {
    if (attrs.q) search = attrs.q
    if (attrs.sort) sort = attrs.sort
    if (attrs.tags) includeTagIds = new Set(attrs.tags.split(','))
    if (attrs.xtags) excludeTagIds = new Set(attrs.xtags.split(','))
    if (attrs.tagMode) tagMode = attrs.tagMode
    if (attrs.from) dateFrom = attrs.from
    if (attrs.to) dateTo = attrs.to
    if (attrs.minMsg !== undefined && attrs.minMsg !== '') minMessages = attrs.minMsg
    if (attrs.maxMsg !== undefined && attrs.maxMsg !== '') maxMessages = attrs.maxMsg
    if (attrs.untagged === '1') filterUntagged = true
    if (attrs.bm === '1') filterBookmarked = true
    if (attrs.mine === '1') filterMyActivity = true
    if (attrs.eval) evalFilter = attrs.eval
    if (attrs.rubric) evalRubricId = attrs.rubric
    if (attrs.p) page = parseInt(attrs.p, 10) || 0
    // Auto-open advanced filter panel if any advanced filter is active
    if (dateFrom || dateTo || minMessages !== '' || maxMessages !== '' ||
        filterUntagged || filterBookmarked || filterMyActivity || evalFilter !== 'any') {
      showFilters = true
    }
  }

  function buildParams() {
    const params = {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      sort,
    }
    if (search.trim()) params.search = search.trim()
    if (includeTagIds.size > 0) {
      params.tagIds = [...includeTagIds].join(',')
      params.tagMode = tagMode
    }
    if (excludeTagIds.size > 0) {
      params.excludeTagIds = [...excludeTagIds].join(',')
    }
    if (dateFrom) params.dateFrom = dateFrom
    if (dateTo) params.dateTo = dateTo + 'T23:59:59'
    if (minMessages !== '' && !isNaN(parseInt(minMessages, 10))) params.minMessages = parseInt(minMessages, 10)
    if (maxMessages !== '' && !isNaN(parseInt(maxMessages, 10))) params.maxMessages = parseInt(maxMessages, 10)
    if (filterUntagged) params.untagged = '1'
    if (filterBookmarked) params.bookmarked = '1'
    if (filterMyActivity) params.myActivity = '1'
    if (evalFilter === 'evaluated') params.evaluated = 'yes'
    else if (evalFilter === 'unevaluated') params.evaluated = 'no'
    else if (evalFilter === 'failed_critical') params.evaluated = 'failed_critical'
    else if (evalFilter === 'failed_important') params.evaluated = 'failed_important'
    if (evalRubricId) params.evalRubricId = evalRubricId
    return params
  }

  async function loadSessions() {
    syncUrlToState()
    loading = true
    m.redraw()
    try {
      const params = buildParams()
      const result = await sessionsApi.list(projectId, params)
      sessionItems = result.sessions || []
      total = result.total || 0
      selectedIndex = sessionItems.length > 0 ? 0 : -1
    } catch (err) {
      console.error('Failed to load sessions:', err)
      sessionItems = []
      total = 0
    } finally {
      loading = false
      m.redraw()
    }
  }

  function scheduleRecordSearch() {
    clearTimeout(recordDebounce)
    recordDebounce = setTimeout(() => {
      const params = buildParams()
      const hasSearch = params.search
      const hasFilters = params.tagIds || params.excludeTagIds ||
        params.dateFrom || params.dateTo ||
        params.minMessages != null || params.maxMessages != null ||
        params.untagged || params.bookmarked || params.myActivity || params.evaluated
      if (projectId && (hasSearch || hasFilters)) {
        searchHistoryApi.record(projectId, {
          query: params.search || '',
          filters: params,
          resultCount: total,
        }).catch(() => {})
      }
    }, 2000)
  }

  function onSearch(value) {
    search = value
    page = 0
    clearTimeout(searchDebounce)
    searchDebounce = setTimeout(loadSessions, 300)
    scheduleRecordSearch()
  }

  function applyFilters() {
    page = 0
    loadSessions()
    scheduleRecordSearch()
  }

  function clearAllFilters() {
    dateFrom = ''
    dateTo = ''
    minMessages = ''
    maxMessages = ''
    filterUntagged = false
    filterBookmarked = false
    filterMyActivity = false
    includeTagIds = new Set()
    excludeTagIds = new Set()
    tagMode = 'any'
    evalFilter = 'any'
    evalRubricId = ''
    page = 0
    loadSessions()
  }

  function activeFilterCountNum() {
    let count = 0
    if (dateFrom) count++
    if (dateTo) count++
    if (minMessages !== '') count++
    if (maxMessages !== '') count++
    if (filterUntagged) count++
    if (filterBookmarked) count++
    if (filterMyActivity) count++
    if (includeTagIds.size > 0) count++
    if (excludeTagIds.size > 0) count++
    if (evalFilter !== 'any') count++
    return count
  }

  // Tag filter: cycle through off → include → exclude → off
  function cycleTag(tagId) {
    if (includeTagIds.has(tagId)) {
      includeTagIds.delete(tagId)
      excludeTagIds.add(tagId)
    } else if (excludeTagIds.has(tagId)) {
      excludeTagIds.delete(tagId)
    } else {
      includeTagIds.add(tagId)
    }
    filterUntagged = false
    page = 0
    loadSessions()
  }

  function removeTagFilter(tagId) {
    includeTagIds.delete(tagId)
    excludeTagIds.delete(tagId)
    page = 0
    loadSessions()
  }

  function getTagState(tagId) {
    if (includeTagIds.has(tagId)) return 'include'
    if (excludeTagIds.has(tagId)) return 'exclude'
    return 'off'
  }

  function goToSession(session) {
    try {
      sessionStorage.setItem('browserContext', JSON.stringify({
        projectId,
        sessionIds: sessionItems.map((s) => s.id),
        total,
        page,
        pageSize: PAGE_SIZE,
        search: search.trim() || null,
        sort,
        filterTagIds: includeTagIds.size > 0 ? [...includeTagIds] : null,
        excludeTagIds: excludeTagIds.size > 0 ? [...excludeTagIds] : null,
        tagMode,
        returnUrl: `/projects/${projectId}/browser` + buildQueryString(),
      }))
    } catch { /* ignore */ }
    m.route.set(`/projects/${projectId}/sessions/${session.id}`)
  }

  function onKeyDown(e) {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (selectedIndex < sessionItems.length - 1) {
        selectedIndex++
        scrollSelectedIntoView()
        m.redraw()
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (selectedIndex > 0) {
        selectedIndex--
        scrollSelectedIntoView()
        m.redraw()
      }
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault()
      goToSession(sessionItems[selectedIndex])
    }
  }

  function scrollSelectedIntoView() {
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-session-index="${selectedIndex}"]`)
      if (el) el.scrollIntoView({ block: 'nearest' })
    })
  }

  const totalPages = () => Math.max(1, Math.ceil(total / PAGE_SIZE))
  const start = () => total === 0 ? 0 : page * PAGE_SIZE + 1
  const end = () => Math.min((page + 1) * PAGE_SIZE, total)

  // Filtered tags for dropdown typeahead
  function filteredTags() {
    if (!tagSearch.trim()) return allTags
    const q = tagSearch.toLowerCase()
    return allTags.filter(t => t.name.toLowerCase().includes(q))
  }

  // Tags that are actively selected (include or exclude)
  function selectedTags() {
    return allTags.filter(t => includeTagIds.has(t.id) || excludeTagIds.has(t.id))
  }

  function renderTagFilter() {
    const selected = selectedTags()
    const hasTagFilters = selected.length > 0

    return m('div', { class: tagFilterContainer }, [
      // Button to open dropdown
      m('button', {
        class: tagFilterBtn,
        style: hasTagFilters ? `border-color: ${colors.brand};` : '',
        onclick: (e) => {
          e.stopPropagation()
          tagDropdownOpen = !tagDropdownOpen
          tagSearch = ''
        },
      }, [
        'Tags',
        hasTagFilters
          ? m('span', { class: activeFilterCount }, selected.length)
          : null,
      ]),

      // Dropdown
      tagDropdownOpen
        ? m('div', {
            class: tagDropdown,
            onclick: (e) => e.stopPropagation(),
          }, [
            m('input', {
              class: tagSearchInput,
              type: 'text',
              placeholder: 'Search tags...',
              value: tagSearch,
              oninput: (e) => { tagSearch = e.target.value },
              oncreate: (vnode) => { vnode.dom.focus() },
              onkeydown: (e) => {
                if (e.key === 'Escape') {
                  tagDropdownOpen = false
                  e.stopPropagation()
                }
              },
            }),
            m('div', { class: tagDropdownList }, [
              // Untagged option
              m('div', {
                key: 'untagged',
                class: tagDropdownItem,
                onclick: () => {
                  filterUntagged = !filterUntagged
                  if (filterUntagged) {
                    includeTagIds = new Set()
                    excludeTagIds = new Set()
                  }
                  page = 0
                  loadSessions()
                },
              }, [
                m('div', {
                  class: tagDropdownSwatch,
                  style: `background: ${colors.textMuted};`,
                }),
                m('span', { class: tagDropdownName }, 'Untagged'),
                filterUntagged
                  ? m('span', {
                      class: tagStateIndicator,
                      style: `background: ${colors.brand}; color: white;`,
                    }, '\u2713')
                  : null,
              ]),
              // Tag list
              ...filteredTags().map(t => {
                const state = getTagState(t.id)
                return m('div', {
                  key: t.id,
                  class: tagDropdownItem,
                  onclick: () => cycleTag(t.id),
                }, [
                  m('div', {
                    class: tagDropdownSwatch,
                    style: `background: ${t.color || colors.border};`,
                  }),
                  m('span', { class: tagDropdownName }, t.name),
                  t.shortcut
                    ? m('span', {
                        style: `font-size: 10px; color: ${colors.textMuted}; font-family: 'SF Mono', monospace;`,
                      }, t.shortcut.toUpperCase())
                    : null,
                  state === 'include'
                    ? m('span', {
                        class: tagStateIndicator,
                        style: `background: #a9e34b30; color: #5a8a00;`,
                        title: 'Including - click to exclude',
                      }, '+')
                    : state === 'exclude'
                      ? m('span', {
                          class: tagStateIndicator,
                          style: `background: ${colors.critical}20; color: ${colors.critical};`,
                          title: 'Excluding - click to clear',
                        }, '\u2212')
                      : null,
                ])
              }),
            ]),
          ])
        : null,
    ])
  }

  function renderSelectedChips() {
    const selected = selectedTags()
    if (selected.length === 0 && !filterUntagged) return null

    const chips = []
    if (filterUntagged) {
      chips.push(m('span', {
        key: 'untagged',
        class: selectedTagChip,
        style: `background: ${colors.textMuted}30; color: ${colors.text}; border: 1px solid ${colors.textMuted};`,
        onclick: () => {
          filterUntagged = false
          page = 0
          loadSessions()
        },
        title: 'Click to remove',
      }, [
        'Untagged',
        m('span', { style: 'margin-left: 2px; opacity: 0.6;' }, '\u00d7'),
      ]))
    }
    for (const t of selected) {
      const state = getTagState(t.id)
      const isExclude = state === 'exclude'
      chips.push(m('span', {
        key: t.id,
        class: selectedTagChip,
        style: isExclude
          ? `background: ${colors.critical}15; color: ${colors.critical}; border: 1px solid ${colors.critical}40; text-decoration: line-through;`
          : `background: ${t.color || colors.border}30; color: ${colors.text}; border: 1px solid ${t.color || colors.border};`,
        onclick: () => removeTagFilter(t.id),
        title: isExclude ? 'Excluding - click to remove' : 'Including - click to remove',
      }, [
        isExclude ? '\u2212 ' : '',
        t.name,
        m('span', { style: 'margin-left: 2px; opacity: 0.6;' }, '\u00d7'),
      ]))
    }
    // Mode toggle when 2+ include tags
    if (includeTagIds.size > 1) {
      chips.push(m('button', {
        key: 'mode-toggle',
        class: tagModeToggle,
        title: tagMode === 'any' ? 'Showing sessions with ANY included tag' : 'Showing sessions with ALL included tags',
        onclick: () => { tagMode = tagMode === 'any' ? 'all' : 'any'; page = 0; loadSessions() },
      }, tagMode === 'any' ? 'Any' : 'All'))
    }
    return m('div', { class: selectedTagChips }, chips)
  }

  // Close dropdown when clicking outside
  function onDocClick() {
    let changed = false
    if (tagDropdownOpen) { tagDropdownOpen = false; changed = true }
    if (searchDropdownOpen) { searchDropdownOpen = false; savingSearch = false; changed = true }
    if (changed) m.redraw()
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      restoreStateFromUrl(vnode.attrs)
      loadProject()
      loadTags()
      loadRubrics()
      loadSearchHistory()
      loadSessions()
    },

    oncreate() {
      document.addEventListener('keydown', onKeyDown)
      document.addEventListener('click', onDocClick)
    },

    onremove() {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('click', onDocClick)
      clearTimeout(searchDebounce)
    },

    view(vnode) {
      projectId = vnode.attrs.projectId
      const filterCount = activeFilterCountNum()

      return m('div', { class: browserWrap }, [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project?.name || 'Project'),
          ' / ',
          'Sessions',
        ]),

        // Toolbar: search + sort + tag filter + filters toggle + counter
        m('div', { class: toolbar }, [
          m('div', {
            class: searchWrap,
            onclick: (e) => e.stopPropagation(),
            onfocusout: (e) => {
              // Close dropdown when focus leaves the search container entirely
              if (!e.currentTarget.contains(e.relatedTarget)) {
                searchDropdownOpen = false
                savingSearch = false
                m.redraw()
              }
            },
          }, [
            m('input', {
              class: searchInput,
              style: 'width: 100%;',
              type: 'text',
              placeholder: 'Search by session ID or message content...',
              value: search,
              oninput: (e) => onSearch(e.target.value),
              onfocus: () => {
                if (recentSearches.length > 0 || savedSearches.length > 0) {
                  searchDropdownOpen = true
                }
              },
            }),
            searchDropdownOpen && (recentSearches.length > 0 || savedSearches.length > 0)
              ? m('div', {
                  class: tagDropdown,
                  style: 'width: min(400px, calc(100vw - 2rem));',
                  onmousedown: (e) => e.preventDefault(),
                }, [
                  // Save current search button
                  (search.trim() || activeFilterCountNum() > 0)
                    ? (() => {
                        if (savingSearch) {
                          return m('div', { style: `padding: ${space.sm} ${space.md}; border-bottom: 1px solid ${colors.border}; display: flex; gap: ${space.xs};` }, [
                            m('input', {
                              class: tagSearchInput,
                              style: 'border: 1px solid ' + colors.border + '; border-radius: 4px; flex: 1;',
                              placeholder: 'Search name...',
                              value: saveSearchName,
                              oninput: (e) => { saveSearchName = e.target.value },
                              oncreate: (vnode) => vnode.dom.focus(),
                              onkeydown: (e) => {
                                if (e.key === 'Enter') { saveCurrentSearch(); e.preventDefault() }
                                if (e.key === 'Escape') { savingSearch = false; e.stopPropagation() }
                              },
                            }),
                            m('button', {
                              style: `${btnSecondary}; padding: 4px 8px; font-size: ${font.size.xs};`,
                              onclick: saveCurrentSearch,
                            }, 'Save'),
                          ])
                        }
                        return m('div', {
                          style: `padding: 6px ${space.md}; border-bottom: 1px solid ${colors.border}; font-size: ${font.size.sm}; color: ${colors.brand}; cursor: pointer;`,
                          onclick: () => { savingSearch = true },
                        }, '\u2606 Save current search')
                      })()
                    : null,
                  // Saved searches
                  savedSearches.length > 0
                    ? m('div', [
                        m('div', { style: `padding: 6px ${space.md} 2px; font-size: ${font.size.xs}; color: ${colors.textMuted}; font-weight: ${font.weight.semibold}; text-transform: uppercase; letter-spacing: 0.05em;` }, 'Saved'),
                        ...savedSearches.map(s =>
                          m('div', {
                            class: tagDropdownItem,
                            onclick: () => applySearch(s),
                          }, [
                            m('span', { style: `color: ${colors.brand}; margin-right: 4px;` }, '\u2605'),
                            m('span', { class: tagDropdownName }, s.name),
                            m('span', {
                              style: `font-size: ${font.size.xs}; color: ${colors.textMuted}; cursor: pointer; padding: 2px;`,
                              onclick: (e) => deleteSavedSearch(s.id, e),
                              title: 'Delete saved search',
                            }, '\u2715'),
                          ])
                        ),
                      ])
                    : null,
                  // Recent searches
                  recentSearches.length > 0
                    ? m('div', [
                        m('div', { style: `padding: 6px ${space.md} 2px; font-size: ${font.size.xs}; color: ${colors.textMuted}; font-weight: ${font.weight.semibold}; text-transform: uppercase; letter-spacing: 0.05em;` }, 'Recent'),
                        ...recentSearches.map(h =>
                          m('div', {
                            class: tagDropdownItem,
                            onclick: () => applySearch(h),
                          }, [
                            m('span', { class: tagDropdownName }, h.query || '(filters only)'),
                            h.result_count != null
                              ? m('span', { style: `font-size: ${font.size.xs}; color: ${colors.textMuted};` },
                                  `${h.result_count} results`)
                              : null,
                          ])
                        ),
                      ])
                    : null,
                ])
              : null,
          ]),
          m('select', {
            class: sortSelect,
            value: sort,
            onchange: (e) => { sort = e.target.value; page = 0; loadSessions() },
          }, [
            m('option', { value: 'newest' }, 'Newest first'),
            m('option', { value: 'oldest' }, 'Oldest first'),
            m('option', { value: 'messages' }, 'Most messages'),
          ]),
          allTags.length > 0 ? renderTagFilter() : null,
          m('button', {
            class: toggleFiltersBtn,
            onclick: () => { showFilters = !showFilters },
          }, [
            'Filters',
            filterCount > 0
              ? m('span', { class: activeFilterCount }, filterCount)
              : null,
          ]),
          total > 0
            ? m('span', { class: counterStyle },
                `${start()}\u2013${end()} of ${total}`)
            : null,
        ]),

        // Selected tag chips row
        renderSelectedChips(),

        // Filter panel (collapsible)
        showFilters
          ? m('div', { class: filterPanelStyle }, [
              m('span', { class: filterLabel }, 'Date:'),
              m('input', {
                class: filterInput,
                type: 'date',
                value: dateFrom,
                onchange: (e) => { dateFrom = e.target.value; applyFilters() },
                title: 'From date',
              }),
              m('span', { style: `color: ${colors.textMuted}; font-size: ${font.size.xs};` }, 'to'),
              m('input', {
                class: filterInput,
                type: 'date',
                value: dateTo,
                onchange: (e) => { dateTo = e.target.value; applyFilters() },
                title: 'To date',
              }),
              m('span', { class: filterDivider }),
              m('span', { class: filterLabel }, 'Messages:'),
              m('input', {
                class: filterInput,
                style: 'width: 70px;',
                type: 'number',
                placeholder: 'Min',
                min: 0,
                value: minMessages,
                onchange: (e) => { minMessages = e.target.value; applyFilters() },
              }),
              m('span', { style: `color: ${colors.textMuted}; font-size: ${font.size.xs};` }, '\u2013'),
              m('input', {
                class: filterInput,
                style: 'width: 70px;',
                type: 'number',
                placeholder: 'Max',
                min: 0,
                value: maxMessages,
                onchange: (e) => { maxMessages = e.target.value; applyFilters() },
              }),
              m('span', { class: filterDivider }),
              m('span', { class: filterLabel }, 'Evaluated:'),
              m('select', {
                class: sortSelect,
                style: `font-size: ${font.size.xs}; padding: 4px ${space.sm};`,
                value: evalFilter,
                onchange: (e) => { evalFilter = e.target.value; if (evalFilter === 'any') evalRubricId = ''; page = 0; loadSessions() },
              }, [
                m('option', { value: 'any' }, 'Any'),
                m('option', { value: 'evaluated' }, 'Evaluated'),
                m('option', { value: 'unevaluated' }, 'Not evaluated'),
                m('option', { value: 'failed_critical' }, 'Failed Critical'),
                m('option', { value: 'failed_important' }, 'Failed Important'),
              ]),
              rubricList.length > 0 && evalFilter !== 'any'
                ? m('select', {
                    class: sortSelect,
                    style: `font-size: ${font.size.xs}; padding: 4px ${space.sm};`,
                    value: evalRubricId,
                    onchange: (e) => { evalRubricId = e.target.value; page = 0; loadSessions() },
                  }, [
                    m('option', { value: '' }, 'Any rubric'),
                    ...rubricList.map(r =>
                      m('option', { value: r.id }, r.title)),
                  ])
                : null,
              m('span', { class: filterDivider }),
              m('button', {
                class: clearFiltersBtn,
                style: filterBookmarked ? `color: ${colors.brand}; border-color: ${colors.brand};` : '',
                onclick: () => { filterBookmarked = !filterBookmarked; page = 0; loadSessions() },
              }, '\u2606 Bookmarked'),
              m('button', {
                class: clearFiltersBtn,
                style: filterMyActivity ? `color: ${colors.brand}; border-color: ${colors.brand};` : '',
                onclick: () => { filterMyActivity = !filterMyActivity; page = 0; loadSessions() },
              }, 'My sessions'),
              filterCount > 0
                ? m('button', {
                    class: clearFiltersBtn,
                    onclick: clearAllFilters,
                  }, 'Clear filters')
                : null,
            ])
          : null,

        // Session list
        m('div', { class: sessionList }, (() => {
          if (loading) {
            return [m('div', { key: '__loading__', class: emptyState }, 'Loading...')]
          }
          if (sessionItems.length === 0) {
            return [m('div', { key: '__empty__', class: emptyState }, [
              search
                ? `No sessions matching "${search}"`
                : filterCount > 0
                  ? 'No sessions match the current filters.'
                  : 'No sessions yet. Upload data to get started.',
            ])]
          }
          return sessionItems.map((session, idx) =>
                  m('div', {
                    key: session.id,
                    class: `${sessionRow} ${idx === selectedIndex ? sessionRowSelected : ''}`,
                    'data-session-index': idx,
                    onclick: () => goToSession(session),
                    onmouseenter: () => { selectedIndex = idx },
                  }, [
                    m('div', { class: sessionInfo }, [
                      m('div', { class: sessionIdStyle }, [
                        session.bookmarked
                          ? m('span', {
                              style: `color: ${colors.brand}; margin-right: 4px;`,
                              title: 'Bookmarked',
                            }, '\u2605')
                          : null,
                        session.external_id || session.id.slice(0, 8),
                      ]),
                      m('div', { class: sessionMeta }, [
                        session.timestamp
                          ? m('span', new Date(session.timestamp).toLocaleDateString())
                          : m('span', new Date(session.created_at).toLocaleDateString()),
                        m('span', `${session.message_count} messages`),
                      ]),
                      session.first_message_preview
                        ? m('div', { class: previewStyle }, session.first_message_preview)
                        : null,
                    ]),
                    m('div', { class: rightCol }, (() => {
                      const children = []
                      if (session.tags && session.tags.length > 0) {
                        // Desktop: full tag chips
                        children.push(
                          m('div', { key: 'tags-desktop', class: tagChipsDesktop },
                            session.tags.map(tag =>
                              m('span', {
                                key: tag.id,
                                class: tagChip,
                                style: `background: ${tag.color || colors.border}; color: ${colors.text};${tag.source === 'ai' ? ' border: 1px dashed rgba(0,0,0,0.2);' : ''}`,
                                title: tag.source === 'ai' ? 'AI-predicted' : '',
                              }, tag.name)
                            )
                          )
                        )
                        // Mobile: compact badge
                        if (session.tags.length === 1) {
                          const tag = session.tags[0]
                          children.push(
                            m('span', {
                              key: 'tags-mobile',
                              class: tagCountBadge,
                              style: `background: ${tag.color || colors.border}; color: ${colors.text};`,
                            }, tag.name)
                          )
                        } else {
                          children.push(
                            m('span', {
                              key: 'tags-mobile',
                              class: tagCountBadge,
                              title: session.tags.map(t => t.name).join(', '),
                            }, `${session.tags.length} tags`)
                          )
                        }
                      }
                      if (isOwner) {
                        children.push(
                          m('button', {
                            key: 'delete',
                            class: deleteBtn,
                            title: 'Delete session',
                            onclick: (e) => deleteSession(session.id, e),
                          }, '\u00d7')
                        )
                      }
                      return children
                    })()),
                  ])
                )
        })()),

        // Pagination
        total > PAGE_SIZE
          ? m('div', { class: paginationBar }, [
              m('button', {
                class: btnSecondary,
                disabled: page === 0,
                onclick: () => { page--; loadSessions() },
              }, 'Previous'),
              m('span', { style: `font-size: ${font.size.sm}; color: ${colors.textMuted};` },
                `Page ${page + 1} of ${totalPages()}`),
              m('button', {
                class: btnSecondary,
                disabled: page >= totalPages() - 1,
                onclick: () => { page++; loadSessions() },
              }, 'Next'),
            ])
          : null,

        // Keyboard shortcut hint
        sessionItems.length > 0
          ? m('div', {
              style: `text-align: center; font-size: ${font.size.xs}; color: ${colors.textMuted}; padding: ${space.xs} 0;`,
            }, '\u2191\u2193 to navigate, Enter to open')
          : null,
      ])
    },
  }
}
